import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { TEMPERATURE_CONSTANTS, UI_CONSTANTS } from './constants';
import { useAutomationStore } from '@/stores/automationStore';

// Define interfaces for the types used in this file
export interface TemperatureData {
    value?: number | null;
    unit?: 'C' | 'F' | null;
    rawInput: string;
}

export interface UnitCheckData {
    billedSetpoint?: TemperatureData;
    actualSetpoint?: TemperatureData;
    returnTemp?: TemperatureData;
    supplyTemp?: TemperatureData;
    [key: string]: TemperatureData | undefined;
}

// Enhanced interface for temperature values
export interface Temperature {
    value: number;
    unit: 'C' | 'F';
}

/**
 * Helper function to convert temperature to Celsius
 */
export function convertToCelsius(temperature: Temperature): number {
    if (temperature.unit === 'C') {
        return temperature.value;
    }
    return (temperature.value - TEMPERATURE_CONSTANTS.CONVERSION.FAHRENHEIT_OFFSET) * TEMPERATURE_CONSTANTS.CONVERSION.CELSIUS_MULTIPLIER;
}

/**
 * Helper function to convert temperature difference to Celsius
 * For differences/thresholds, we don't subtract 32 (only multiply by 5/9)
 */
export function convertDifferenceToCelsius(temperature: Temperature): number {
    if (temperature.unit === 'C') {
        return temperature.value;
    }
    return temperature.value * TEMPERATURE_CONSTANTS.CONVERSION.CELSIUS_MULTIPLIER;
}

/**
 * Helper function to compare temperature values with a threshold
 * Refactored to use fewer parameters by grouping related data
 */
export function tempWithinThreshold(
    temp1: Temperature,
    temp2: Temperature,
    threshold: Temperature
): boolean {
    // Convert absolute temperatures to Celsius for comparison
    const temp1C = convertToCelsius(temp1);
    const temp2C = convertToCelsius(temp2);
    // Convert threshold (a difference) to Celsius using the difference conversion
    const thresholdC = convertDifferenceToCelsius(threshold);

    // Calculate the absolute difference
    const difference = Math.abs(temp1C - temp2C);

    // Compare the difference with the threshold
    return difference <= thresholdC;
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use tempWithinThreshold with Temperature objects instead
 */
export function tempWithinThresholdLegacy(
    temp1: number,
    unit1: 'C' | 'F',
    temp2: number,
    unit2: 'C' | 'F',
    threshold: number,
    thresholdUnit: 'C' | 'F'
): boolean {
    return tempWithinThreshold(
        { value: temp1, unit: unit1 },
        { value: temp2, unit: unit2 },
        { value: threshold, unit: thresholdUnit }
    );
}

/**
 * Composable for temperature validation and comparison
 */
export function useTemperatureValidation() {
    const unitCheckData = ref<UnitCheckData>({});
    const invalidFields = ref<Set<string>>(new Set());
    const temperatureDiscrepancies = ref<Set<string>>(new Set());
    const toast = useToast();
    const automationStore = useAutomationStore?.() as ReturnType<typeof useAutomationStore> | undefined;

    // Use constants from constants file
    const TEMPERATURE_THRESHOLDS = {
        SETPOINT_DISCREPANCY: { 
            value: automationStore?.setpointTolerance ?? TEMPERATURE_CONSTANTS.VALIDATION.SETPOINT_DISCREPANCY, 
            unit: 'C' as const 
        },
        OUT_OF_RANGE: { 
            value: automationStore?.returnTempTolerance ?? TEMPERATURE_CONSTANTS.VALIDATION.OUT_OF_RANGE, 
            unit: 'C' as const 
        }
    } as const;

    // Toast messages configuration
    const TOAST_MESSAGES = {
        setpoint: {
            severity: 'warn' as const,
            summary: 'Setpoint Discrepancy!',
            getDetail: (threshold: number, unit: string) => 
                `The actual setpoint is not within ${threshold}°${unit} of the billed setpoint.`
        },
        returnTemp: {
            severity: 'warn' as const,
            summary: 'Return Temp Out Of Range!',
            getDetail: (threshold: number, unit: string) => 
                `The return temperature is not within ${threshold}°${unit} of the setpoint.`
        }
    } as const;

    /**
     * Checks if two temperature values are valid for comparison
     */
    function areTemperaturesValid(temp1?: TemperatureData, temp2?: TemperatureData): boolean {
        return temp1?.value != null && temp2?.value != null;
    }

    /**
     * Shows toast notification for temperature discrepancy
     */
    function showDiscrepancyToast(type: keyof typeof TOAST_MESSAGES, threshold: number, unit: string) {
        const config = TOAST_MESSAGES[type];
        toast.add({
            severity: config.severity,
            summary: config.summary,
            detail: config.getDetail(threshold, unit),
            life: UI_CONSTANTS.TOAST_LIFE.MEDIUM
        });
    }

    /**
     * Checks for setpoint discrepancy between billed and actual setpoint
     */
    function checkSetpointDiscrepancy(billedSetpoint: TemperatureData, actualSetpoint: TemperatureData) {
        const threshold = TEMPERATURE_THRESHOLDS.SETPOINT_DISCREPANCY;
        
        const withinThreshold = tempWithinThreshold(
            { value: billedSetpoint.value!, unit: billedSetpoint.unit || 'C' },
            { value: actualSetpoint.value!, unit: actualSetpoint.unit || 'C' },
            { value: threshold.value, unit: threshold.unit }
        );

        if (!withinThreshold) {
            temperatureDiscrepancies.value.add('setpoint');
            showDiscrepancyToast('setpoint', threshold.value, threshold.unit);
        }
    }

    /**
     * Checks for return temperature being out of range from setpoint
     */
    function checkReturnTempRange(actualSetpoint: TemperatureData, returnTemp: TemperatureData) {
        const threshold = TEMPERATURE_THRESHOLDS.OUT_OF_RANGE;
        
        const withinThreshold = tempWithinThreshold(
            { value: returnTemp.value!, unit: returnTemp.unit || 'C' },
            { value: actualSetpoint.value!, unit: actualSetpoint.unit || 'C' },
            { value: threshold.value, unit: threshold.unit }
        );

        if (!withinThreshold) {
            temperatureDiscrepancies.value.add('return-temp');
            showDiscrepancyToast('returnTemp', threshold.value, threshold.unit);
        }
    }

    /**
     * Checks for temperature discrepancies between different temperature readings
     * Simplified with extracted helper functions
     */
    function checkTemperatureDiscrepancies() {
        const { billedSetpoint, actualSetpoint, returnTemp } = unitCheckData.value;

        // Clear any existing discrepancies
        temperatureDiscrepancies.value.clear();

        // Check setpoint discrepancy
        if (areTemperaturesValid(billedSetpoint, actualSetpoint)) {
            checkSetpointDiscrepancy(billedSetpoint!, actualSetpoint!);
        }

        // Check return temperature range
        if (areTemperaturesValid(actualSetpoint, returnTemp)) {
            checkReturnTempRange(actualSetpoint!, returnTemp!);
        }
    }

    /**
     * Validates a temperature field input
     */
    function validateTemperatureField(field: keyof UnitCheckData): boolean {
        const data = unitCheckData.value[field] as TemperatureData | undefined;

        if (!data || !data.rawInput || !data.rawInput.trim()) {
            invalidFields.value.delete(String(field));
            if (data) {
                data.value = null;
                data.unit = null;
            }
            return true; // Consider empty field as valid
        }

        const match = data.rawInput.match(/^(-?\d+(\.\d+)?)(C|F)$/i);
        if (match) {
            const [, numPart, , unitPart] = match;
            const numValue = parseFloat(numPart);
            const unitValue = unitPart.toUpperCase() as 'C' | 'F';

            unitCheckData.value[field] = {
                value: numValue,
                unit: unitValue,
                rawInput: data.rawInput
            };

            invalidFields.value.delete(String(field));
            checkTemperatureDiscrepancies();
            return true;
        }

        invalidFields.value.add(String(field));
        toast.add({
            severity: 'error',
            summary: 'Invalid Temperature',
            detail: `${field} must be a number followed by C or F`,
            life: UI_CONSTANTS.TOAST_LIFE.MEDIUM
        });
        return false;
    }

    return {
        unitCheckData,
        invalidFields,
        temperatureDiscrepancies,
        validateTemperatureField,
        checkTemperatureDiscrepancies
    };
}
